import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserTable = ({ users, onUserAction, selectedUsers, onUserSelect, onSelectAll }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    if (!sortConfig?.key) return users;

    return [...users]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (sortConfig?.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [users, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getSubscriptionBadge = (subscription) => {
    const subConfig = {
      pro: { color: 'bg-purple-100 text-purple-800', label: 'Pro' },
      basic: { color: 'bg-blue-100 text-blue-800', label: 'Basic' },
      free: { color: 'bg-gray-100 text-gray-800', label: 'Free' },
      enterprise: { color: 'bg-indigo-100 text-indigo-800', label: 'Enterprise' }
    };

    const config = subConfig?.[subscription] || subConfig?.free;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={selectedUsers?.length === users?.length && users?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  indeterminate={selectedUsers?.length > 0 && selectedUsers?.length < users?.length}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 hover:text-foreground transition-micro"
                >
                  <span>User</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('subscription')}
                  className="flex items-center space-x-1 hover:text-foreground transition-micro"
                >
                  <span>Subscription</span>
                  {getSortIcon('subscription')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('registrationDate')}
                  className="flex items-center space-x-1 hover:text-foreground transition-micro"
                >
                  <span>Registered</span>
                  {getSortIcon('registrationDate')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('lastActivity')}
                  className="flex items-center space-x-1 hover:text-foreground transition-micro"
                >
                  <span>Last Activity</span>
                  {getSortIcon('lastActivity')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-1 hover:text-foreground transition-micro"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/30 transition-micro">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => onUserSelect(user?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
                      {user?.avatar ? (
                        <img src={user?.avatar} alt={user?.avatarAlt} className="w-10 h-10 object-cover" />
                      ) : (
                        <Icon name="User" size={20} color="white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{user?.name}</div>
                      <div className="text-sm text-muted-foreground">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {getSubscriptionBadge(user?.subscription)}
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {user?.registrationDate}
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {user?.lastActivity}
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onUserAction('view', user)}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onUserAction('edit', user)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Mail"
                      onClick={() => onUserAction('message', user)}
                    >
                      Message
                    </Button>
                    <Button
                      variant={user?.status === 'suspended' ? 'success' : 'destructive'}
                      size="sm"
                      iconName={user?.status === 'suspended' ? 'UserCheck' : 'UserX'}
                      onClick={() => onUserAction(user?.status === 'suspended' ? 'activate' : 'suspend', user)}
                    >
                      {user?.status === 'suspended' ? 'Activate' : 'Suspend'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedUsers?.map((user) => (
          <div key={user?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedUsers?.includes(user?.id)}
                  onChange={(e) => onUserSelect(user?.id, e?.target?.checked)}
                />
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
                  {user?.avatar ? (
                    <img src={user?.avatar} alt={user?.avatarAlt} className="w-12 h-12 object-cover" />
                  ) : (
                    <Icon name="User" size={24} color="white" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-foreground">{user?.name}</div>
                  <div className="text-sm text-muted-foreground">{user?.email}</div>
                </div>
              </div>
              {getStatusBadge(user?.status)}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <span className="text-muted-foreground">Subscription:</span>
                <div className="mt-1">{getSubscriptionBadge(user?.subscription)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Registered:</span>
                <div className="mt-1 text-foreground">{user?.registrationDate}</div>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Last Activity:</span>
                <div className="mt-1 text-foreground">{user?.lastActivity}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                onClick={() => onUserAction('view', user)}
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => onUserAction('edit', user)}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={() => onUserAction('message', user)}
              >
                Message
              </Button>
              <Button
                variant={user?.status === 'suspended' ? 'success' : 'destructive'}
                size="sm"
                iconName={user?.status === 'suspended' ? 'UserCheck' : 'UserX'}
                onClick={() => onUserAction(user?.status === 'suspended' ? 'activate' : 'suspend', user)}
              >
                {user?.status === 'suspended' ? 'Activate' : 'Suspend'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {sortedUsers?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default UserTable;